/**
 * SmithHeaderActions — Iter256
 *
 * Reusable toolbar that ships the MVP-standard header actions used by
 * the Agency Dashboard (Iter157) to every Smith Protective screen:
 *
 *   [✦ Pedir ajuda da Claire] · [🕒 Last update: 10:29 AM] ·
 *   [🌙 Dark] · [🌐 PT/EN] · [🔄 Refresh] · [🔔 notif bell] ·
 *   [👤 Marcelo · MASTER]
 *
 * Slot it INSIDE `<AppHeader>` `children` after any page-specific
 * buttons. On desktop it lays out inline; on mobile it collapses into
 * the AppHeader kebab menu via the `mobileMenu` prop of AppHeader
 * (each Smith page can pass this component's <MobileMenu> subcomponent).
 *
 * Props:
 *   • claireQuestion   — string the Claire widget pre-fills on click
 *   • lastUpdated      — Date | null shown next to the clock icon
 *   • onRefresh        — () => void; when absent the Refresh button is hidden
 *   • refreshing       — boolean; spins the Refresh icon
 *   • notifCount       — number ≥ 0; badge on the bell (0 hides badge)
 *   • onNotifClick     — () => void; opens the notification panel
 *   • testidPrefix     — string used to namespace all data-testids
 */
import { Clock, RefreshCw } from "lucide-react";
import ClaireHelpButton from "./ClaireHelpButton";
import ThemeToggle from "./ThemeToggle";
import LangToggle from "./LangToggle";
import HeaderUserMenu from "./HeaderUserMenu";
import SmithNotificationBell from "./SmithNotificationBell";
import InstallPromptButton from "./InstallPromptButton";
import PushEnableButton from "./PushEnableButton";


export default function SmithHeaderActions({
  claireQuestion = "Como uso esta tela do Smith Protective?",
  lastUpdated = null,
  onRefresh,
  refreshing = false,
  notifCount = 0,
  onNotifClick,
  testidPrefix = "smith-header",
}) {
  return (
    <>
      <ClaireHelpButton testid={testidPrefix}
                        question={claireQuestion} />
      {lastUpdated && (
        <span data-testid={`${testidPrefix}-last-updated`}
              className="hidden lg:inline-flex items-center gap-1 text-[11px] text-[var(--al-text-muted)] mr-1.5">
          <Clock className="w-3 h-3" />
          Last update:{" "}
          <strong className="text-[var(--al-text)] tabular-nums">
            {lastUpdated.toLocaleTimeString([], { hour: "2-digit",
                                                  minute: "2-digit",
                                                  second: "2-digit" })}
          </strong>
        </span>
      )}
      <ThemeToggle />
      <LangToggle />
      {onRefresh && (
        <button type="button"
                data-testid={`${testidPrefix}-refresh`}
                onClick={onRefresh}
                disabled={refreshing}
                className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-[var(--al-border)] bg-[var(--al-card-bg)] text-[12.5px] font-medium hover:border-[#DC2626] hover:text-[#DC2626] transition-colors disabled:opacity-50">
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </button>
      )}
      {/* Iter257 · Live SSE notification bell — always visible */}
      <SmithNotificationBell testidPrefix={testidPrefix} />
      {/* Iter267 · Push opt-in prompt (auto-hides when subscribed/unsupported) */}
      <PushEnableButton variant="chip" />
      {/* Iter266 · PWA install prompt (auto-hides when unavailable) */}
      <InstallPromptButton variant="chip" />
      <HeaderUserMenu />
    </>
  );
}


/**
 * Mobile-menu companion — same set of actions but stacked vertically to
 * fit inside `AppHeader.mobileMenu`. Pass this as `mobileMenu={<SmithHeaderActionsMobile ... />}`.
 */
export function SmithHeaderActionsMobile({
  claireQuestion = "Como uso esta tela do Smith Protective?",
  lastUpdated = null,
  onRefresh,
  refreshing = false,
  testidPrefix = "smith-header",
}) {
  return (
    <>
      {onRefresh && (
        <button type="button" onClick={onRefresh} disabled={refreshing}
                data-testid={`${testidPrefix}-refresh-mobile`}
                className="w-full flex items-center gap-2 px-3 h-10 rounded-md text-[13px] hover:bg-white/5 disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </button>
      )}
      <div className="flex items-center gap-1 px-2 py-1.5">
        <ClaireHelpButton testid={`${testidPrefix}-mobile`}
                          question={claireQuestion} />
        <ThemeToggle />
        <LangToggle />
      </div>
      {/* Iter266 · PWA install prompt inside kebab (mobile) */}
      <div className="px-2 pt-1 pb-2">
        <InstallPromptButton variant="chip" className="w-full justify-center mobile-full" />
      </div>
      {lastUpdated && (
        <div className="border-t border-[var(--al-border)] mt-1 pt-2 px-3 pb-1.5 text-[10.5px] text-[var(--al-text-muted)] inline-flex items-center gap-1">
          <Clock className="w-3 h-3" />
          Last update:{" "}
          <strong className="text-[var(--al-text)] tabular-nums">
            {lastUpdated.toLocaleTimeString([], { hour: "2-digit",
                                                  minute: "2-digit",
                                                  second: "2-digit" })}
          </strong>
        </div>
      )}
    </>
  );
}
